import { useState, useEffect } from "react";
import { useToast } from "components-lib";
import { API_ENDPOINTS, axiosInstance, getAuthHeaders } from "config/api";

const createPatientCode = () => `PAT${Math.floor(Math.random() * 100000)}`;

/**
 * Custom hook for managing patient form operations
 * Handles form state, validation, and API interactions for patient creation
 */
export const usePatientForm = (clinicId, doctorId, onPatientCallback) => {
  const toast = useToast();

  // Form state (include keys expected by EditPatientForm)
  const [newPatientData, setNewPatientData] = useState(() => {
    const patientCode = createPatientCode();
    return {
      id: patientCode,
      name: "",
      age: "",
      gender: "",
      email: "",
      phoneNumber: "",
      test: "",
      dob: "",
      pastAppointments: [],
      address: "",
      pin: "", // legacy
      pincode: "", // used by EditPatientForm
      preferredLanguage: "", // legacy
      language: "", // used by EditPatientForm
      bloodGroup: "", // legacy
      bloodgroup: "", // used by EditPatientForm
      city: "",
      existingId: "",
      token_no: "",
      clinic: "",
      clinic_id: "",
      doctor_id: "",
      patient_code: patientCode
    };
  });

  // Error handling state
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [createdPatientId, setCreatedPatientId] = useState(null);

  // Legacy method support:
  // Accept either:
  // - a single function (previously onPatientAdded or onPatientUpdated) => treat it as both add/update handler
  // - an object with keys { onPatientAdded, onPatientUpdated }
  let onPatientAddedFn = null;
  let onPatientUpdatedFn = null;

  if (typeof onPatientCallback === "function") {
    // If parent passed a single function (could be onPatientAdded or onPatientUpdated),
    // call it for both add and update to maintain backward compatibility.
    onPatientAddedFn = onPatientCallback;
    onPatientUpdatedFn = onPatientCallback;
  } else if (onPatientCallback && typeof onPatientCallback === "object") {
    if (typeof onPatientCallback.onPatientAdded === "function") {
      onPatientAddedFn = onPatientCallback.onPatientAdded;
    }
    if (typeof onPatientCallback.onPatientUpdated === "function") {
      onPatientUpdatedFn = onPatientCallback.onPatientUpdated;
    }
  }

  // Helper: unified invoker for add event (calls legacy onPatientAdded/onPatientUpdated as required)
  const notifyPatientAdded = (patientId, patientData) => {
    try {
      if (typeof onPatientAddedFn === "function") {
        onPatientAddedFn(patientId, patientData);
      }
      // Also support scenarios where parent expects an "updated" handler to be called on add
      if (
        typeof onPatientUpdatedFn === "function" &&
        onPatientUpdatedFn !== onPatientAddedFn
      ) {
        onPatientUpdatedFn(patientId, patientData);
      }
    } catch (err) {
      console.error("Error invoking patient added/updated handlers:", err);
    }
  };

  // Expose a notifier for update as well (in case callers want to trigger update notifications)
  const notifyPatientUpdated = (patientId, patientData) => {
    try {
      if (typeof onPatientUpdatedFn === "function") {
        onPatientUpdatedFn(patientId, patientData);
      }
      // Also call added handler if it's the same function or parent expects that behavior
      if (
        typeof onPatientAddedFn === "function" &&
        onPatientAddedFn !== onPatientUpdatedFn
      ) {
        onPatientAddedFn(patientId, patientData);
      }
    } catch (err) {
      console.error("Error invoking patient updated/added handlers:", err);
    }
  };

  // Get clinic_id and doctor_id from token if not provided as props
  useEffect(() => {
    if (!clinicId || !doctorId) {
      const tokenPayload = JSON.parse(
        localStorage.getItem("tokenPayload") || "{}"
      );
      const userRole = tokenPayload?.role?.toLowerCase();
      const userRic = tokenPayload?.ric?.toLowerCase();

      // Set clinic_id based on role and props
      let finalClinicId = clinicId;
      let finalDoctorId = doctorId;

      if (!finalClinicId) {
        if (userRole === "front_desk" || userRic === "owner") {
          finalClinicId = tokenPayload?.clinic_id;
        } else if (userRole === "") {
          finalClinicId = tokenPayload?.clinic_id;
          finalDoctorId = tokenPayload?.userId;
        }
      }

      if (!finalDoctorId && userRole === "") {
        finalDoctorId = tokenPayload?.userId;
      }

      setNewPatientData((prevState) => ({
        ...prevState,
        clinic_id: finalClinicId,
        doctor_id: finalDoctorId,
      }));
    } else {
      setNewPatientData((prevState) => ({
        ...prevState,
        clinic_id: clinicId,
        doctor_id: doctorId,
      }));
    }
  }, [clinicId, doctorId]);

  // Handle input changes (keeps both legacy and new keys consistent)
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setNewPatientData((prev) => {
      const updated = { ...prev, [name]: value };

      // Mirror some fields for compatibility
      if (name === "pin") updated.pincode = value;
      if (name === "pincode") updated.pin = value;

      if (name === "preferredLanguage") updated.language = value;
      if (name === "language") updated.preferredLanguage = value;

      if (name === "bloodGroup") updated.bloodgroup = value;
      if (name === "bloodgroup") updated.bloodGroup = value;

      if (name === "phoneNumber") {
        // Also keep phone_no-like legacy if needed
        updated.phoneNumber = value;
      }

      // Auto-calculate Age from DOB
      if (name === "dob") {
        const birthDate = new Date(value);
        const today = new Date();
        let age = today.getFullYear() - birthDate.getFullYear();
        const m = today.getMonth() - birthDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
          age--;
        }
        updated.age = age >= 0 ? age : "";
      }
      // Auto-calculate DOB from Age
      else if (name === "age") {
        // When age is changed, auto-calculate an approximate DOB (YYYY-MM-DD)
        const ageNum = parseInt(value, 10);
        if (!isNaN(ageNum) && ageNum >= 0) {
          const today = new Date();
          // Use today's month/day and subtract years to approximate DOB
          const dobDate = new Date(
            today.getFullYear() - ageNum,
            today.getMonth(),
            today.getDate()
          );
          const yyyy = dobDate.getFullYear();
          const mm = String(dobDate.getMonth() + 1).padStart(2, "0");
          const dd = String(dobDate.getDate()).padStart(2, "0");
          updated.dob = `${yyyy}-${mm}-${dd}`;
          updated.age = ageNum;
        } else {
          updated.dob = "";
          updated.age = value === "" ? "" : value; // keep whatever the user typed if not a valid number
        }
      }

      return updated;
    });
  };

  // Handle file changes (for future use)
  const handleFileChange = (e) => {
    setNewPatientData((prev) => ({ ...prev, profilePhoto: e.target.files[0] }));
  };

  // Form validation
  const validateInputs = () => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^[0-9]{10}$/;
    const pinRegex = /^[0-9]{6}$/;

    if (!newPatientData.name) errors.name = "Name is required.";
    if (!newPatientData.dob) errors.dob = "Date of birth is required.";
    if (!newPatientData.gender) errors.gender = "Gender is required.";

    const phoneVal = newPatientData.phoneNumber || newPatientData.number || "";
    if (!phoneVal) errors.phoneNumber = "Phone number is required.";
    else if (!phoneRegex.test(phoneVal))
      errors.phoneNumber = "Phone number must be 10 digits.";

    // Optional field validations
    if (newPatientData.email && !emailRegex.test(newPatientData.email))
      errors.email = "Invalid email format.";
    const pinVal = newPatientData.pincode || newPatientData.pin || "";
    if (pinVal && !pinRegex.test(pinVal))
      errors.pincode = "Pin code must be 6 digits.";

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setErrorMessage(Object.values(errors)[0]);
      return false;
    }
    setErrorMessage("");
    return true;
  };

  // Handle patient creation
  const handleAddPatient = async () => {
    if (!validateInputs()) {
      return false;
    }

    // Prepare data in the required API format (use fields expected by API)
    const apiPatientData = {
      name: newPatientData.name,
      aliments: "None",
      number: newPatientData.phoneNumber,
      dob: newPatientData.dob,
      registered_date: new Date().toISOString().split("T")[0],
      age: newPatientData.age,
      gender: newPatientData.gender,
      email: newPatientData.email,
      address: newPatientData.address,
      city: newPatientData.city,
      pincode: newPatientData.pincode || newPatientData.pin,
      language: newPatientData.language || newPatientData.preferredLanguage,
      bloodgroup: newPatientData.bloodgroup || newPatientData.bloodGroup,
      clinic: newPatientData.clinic,
      clinic_id: newPatientData.clinic_id,
      doctor_id: newPatientData.doctor_id,
      patient_code: newPatientData.id
    };

    // Store in localStorage before making API call
    const storedPatients = JSON.parse(localStorage.getItem("patients") || "[]");
    const tempId = "temp_" + Date.now();
    const newPatient = {
      id: tempId, // Temporary ID until API response
      patient_id: tempId,
      ...apiPatientData,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      patient_code: newPatientData.id,
      existing_id: newPatientData.existingId || null,
      phone_no: newPatientData.phoneNumber,
      pastAppointments: newPatientData.pastAppointments || [],
    };

    storedPatients.push(newPatient);
    localStorage.setItem("patients", JSON.stringify(storedPatients));

    try {
      const response = await axiosInstance.post(
        API_ENDPOINTS.PATIENT.ADD,
        apiPatientData,
        { headers: getAuthHeaders() }
      );

      // Update the temporary ID with the real one from API response if available
      const updatedPatients = JSON.parse(
        localStorage.getItem("patients") || "[]"
      );
      const idx = updatedPatients.findIndex((p) => p.id === tempId);
      const realId =
        response.data?.data?.patient_id || response.data?.patient_id || null;
      if (idx !== -1 && realId) {
        updatedPatients[idx].id = realId;
        updatedPatients[idx].patient_id = realId;
        localStorage.setItem("patients", JSON.stringify(updatedPatients));
      }

      toast({
        title: "Success",
        description: "Patient added successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      // Determine patientId to store
      const patientId =
        realId || (idx !== -1 ? updatedPatients[idx].id : tempId);

      // Store the created patient ID
      setCreatedPatientId(patientId);

      // Update the patient data with the correct ID
      setNewPatientData((prevState) => ({
        ...prevState,
        id: patientId,
        patient_code: patientId,
      }));

      // Notify caller using legacy methods (ensures onPatientUpdated passed from parent also works)
      const createdPatientData = {
        ...apiPatientData,
        id: patientId,
        patient_id: patientId,
        phone_no: newPatientData.phoneNumber,
      };
      notifyPatientAdded(patientId, createdPatientData);

      return true; // Success
    } catch (error) {
      console.error("Error adding new patient:", error);
      console.error("Error details:", error.response?.data);

      toast({
        title: "Error",
        description:
          error.response?.data?.message ||
          "Error adding new patient. Please check your inputs and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });

      setErrorMessage(
        "Error adding new patient. Please check your inputs and try again."
      );
      return false; // Failure
    }
  };

  // Reset form
  const resetForm = () => {
    const resetCode = createPatientCode();
    setNewPatientData({
      id: resetCode,
      name: "",
      age: "",
      gender: "",
      email: "",
      phoneNumber: "",
      test: "",
      dob: "",
      pastAppointments: [],
      address: "",
      pin: "",
      pincode: "",
      preferredLanguage: "",
      language: "",
      bloodGroup: "",
      bloodgroup: "",
      city: "",
      existingId: "",
      token_no: "",
      clinic: "",
      clinic_id: clinicId || "",
      doctor_id: doctorId || "",
      patient_code: resetCode,
    });
    setFieldErrors({});
    setErrorMessage("");
    setCreatedPatientId(null);
  };

  return {
    // State
    newPatientData,
    setNewPatientData, // exposed for EditPatientForm to populate values
    errorMessage,
    fieldErrors,
    createdPatientId,

    // Actions
    handleInputChange,
    handleFileChange,
    handleAddPatient,
    resetForm,
    validateInputs,

    // Expose notify methods so callers can manually trigger them if needed
    notifyPatientAdded,
    notifyPatientUpdated,
  };
};
